import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Sparkles, RefreshCw, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PolishedNarrativeCard
 * ----------------------------------------------------------
 * Displays the LLM-polished perception-aligned narrative for a subscriber.
 *
 * Variants:
 *   variant="subscriber"   — shows only `subscriber_facing` + headline
 *   variant="admin"        — shows both subscriber-facing and admin-facing
 *                            blocks side by side, with regenerate button
 *
 * Reads via GET /api/decode-narrative/{sub_id}.
 * Admin can trigger POST /generate (create) or /regenerate.
 */
export default function PolishedNarrativeCard({ subscriberId, variant = 'subscriber', onClose }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isCareTeam = user && ['admin', 'counselor', 'health_coach', 'doctor'].includes(user.role);

  const fetchData = async () => {
    if (!subscriberId) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/decode-narrative/${subscriberId}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      if (e.response?.status === 404) setError('not_yet');
      else setError('error');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [subscriberId]);

  const generate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/decode-narrative/${subscriberId}/generate`);
      toast.success('Narrative generated');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate narrative');
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/decode-narrative/${subscriberId}/regenerate`);
      toast.success('Narrative regenerated');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not regenerate narrative');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-[#8A8681]" data-testid="narrative-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading narrative…
      </div>
    );
  }

  // Admin/care-team variant when no narrative exists yet
  if (!data && variant === 'admin' && error === 'not_yet') {
    return (
      <div
        className="rounded-xl border border-dashed border-[#E3DFD7] bg-[#FBFAF7] p-5 flex items-center justify-between gap-3"
        data-testid="narrative-empty-admin"
      >
        <div>
          <p className="text-sm font-['Outfit'] text-[#2C2A29]">No polished narrative yet</p>
          <p className="text-[11px] text-[#8A8681] mt-0.5">
            Generate a perception-aligned narrative based on the latest canonical context, accepted suggestions, and perception reflections.
          </p>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-[#2C2A29] hover:bg-[#1C1A19] text-white text-xs px-4 py-2 font-['Work_Sans']"
          data-testid="narrative-generate-btn"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Generate
        </button>
      </div>
    );
  }

  // Subscriber-facing empty state — quiet
  if (!data && variant === 'subscriber') {
    return null;
  }

  if (!data) return null;

  const polishedDate = data.polished_at ? new Date(data.polished_at).toLocaleDateString() : null;
  const isFallback = data.source === 'fallback';

  // ── Subscriber variant ─────────────────────────────────────────────
  if (variant === 'subscriber') {
    return (
      <div
        className="rounded-2xl border border-[#E3DFD7] bg-gradient-to-b from-white to-[#FAF6EC] p-6"
        data-testid="narrative-subscriber-card"
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-3.5 w-3.5 text-[#8C6B55]" strokeWidth={1.5} />
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8C6B55] font-['Work_Sans']">
            What your system is showing
          </p>
        </div>
        {data.subscriber_headline ? (
          <h3 className="text-xl sm:text-2xl font-['Outfit'] font-light text-[#2C2A29] leading-snug mb-4" data-testid="narrative-subscriber-headline">
            {data.subscriber_headline}
          </h3>
        ) : null}
        <div
          className="text-[14.5px] text-[#3B3936] font-['Work_Sans'] leading-relaxed whitespace-pre-line"
          data-testid="narrative-subscriber-body"
        >
          {data.subscriber_facing}
        </div>
      </div>
    );
  }

  // ── Admin / care-team variant ──────────────────────────────────────
  return (
    <div
      className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-4"
      data-testid="narrative-admin-card"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="h-3.5 w-3.5 text-[#a78bfa]" strokeWidth={1.5} />
        <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-['Work_Sans']">
          Polished Narrative
        </span>
        {isFallback ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-medium"
            data-testid="narrative-fallback-badge"
          >
            <ShieldAlert className="h-3 w-3" /> Deterministic fallback
          </span>
        ) : (
          <span className="text-[10px] text-emerald-300/80">via {data.model || 'gpt-5.2'}</span>
        )}
        {polishedDate ? <span className="text-[10px] text-white/40">{polishedDate}</span> : null}
        {isCareTeam ? (
          <button
            type="button"
            onClick={regenerate}
            disabled={busy}
            className="ml-auto inline-flex items-center gap-1 text-[10.5px] text-white/70 hover:text-white border border-white/10 rounded-full px-2 py-1"
            data-testid="narrative-regenerate-btn"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Regenerate
          </button>
        ) : null}
      </div>

      {data.subscriber_headline ? (
        <div>
          <p className="text-[9.5px] uppercase tracking-[0.2em] text-white/40 mb-1">Subscriber-facing headline</p>
          <p className="text-sm text-white/85 font-['Outfit']" data-testid="narrative-admin-sub-headline">
            {data.subscriber_headline}
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3" data-testid="narrative-admin-sub-block">
          <p className="text-[9.5px] uppercase tracking-[0.2em] text-white/40 mb-1.5">Subscriber-facing narrative</p>
          <p className="text-[12.5px] text-white/85 leading-relaxed font-['Work_Sans'] whitespace-pre-line">
            {data.subscriber_facing}
          </p>
        </div>
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3" data-testid="narrative-admin-clinical-block">
          <p className="text-[9.5px] uppercase tracking-[0.2em] text-white/40 mb-1.5">Admin / clinical narrative</p>
          {data.admin_headline ? (
            <p className="text-[12px] text-[#a78bfa] mb-1.5 font-['Outfit']" data-testid="narrative-admin-headline">
              {data.admin_headline}
            </p>
          ) : null}
          <p className="text-[12.5px] text-white/85 leading-relaxed font-['Work_Sans'] whitespace-pre-line">
            {data.admin_facing}
          </p>
        </div>
      </div>

      <p className="text-[10px] text-white/30">
        Raw structured payload preserved for audit. The polished layer never replaces the deterministic backbone output.
      </p>
    </div>
  );
}
